import type { Paths, UnwrapReadonlyArray } from '@/core/types/gengeric';
import type { FunctionalComponent } from 'vue';
import type { VDataTable } from 'vuetify/components';
import type { HeadersSlotProps } from 'vuetify/lib/components/VDataTable/VDataTableHeaders.mjs';
import type { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort.mjs';
import type { InternalDataTableHeader } from 'vuetify/lib/components/VDataTable/types.mjs';

export type RowProps<T> = Record<string, unknown> | ((data: { item: T }) => Record<string, unknown> | void);

export enum EditableHeaderType {
  Input = 'input',
  Checkbox = 'checkbox',
  Select = 'select',
}

export type EditableHeader<T extends Record<string, unknown> | void = void> = {
  type: EditableHeaderType;
  title: string;
  editableKey: Paths<T>;
  value?: number | boolean | null;
  maska?: string;
  disabled?: boolean;
  isDirty?: boolean;
  selectOptions?: {
    title: string;
    value: unknown;
  }[];
};

export type DataTableHeaders<T extends Record<string, unknown> | void = void> = Array<
  Omit<UnwrapReadonlyArray<VDataTable['$props']['headers']>, 'align'> & {
    align?: 'start' | 'end' | 'center' | ' d-none';
    editable?: EditableHeader<T>;
  }
>;

type ScopedSlotBase<T> = {
  item: T;
  isSelected: (item: T) => boolean;
  internalItem: T;
};

type HeaderSlotProps = {
  column: InternalDataTableHeader;
  selectAll: (value: boolean) => void;
  isSorted: (column: InternalDataTableHeader) => boolean;
  toggleSort: (column: InternalDataTableHeader, event: KeyboardEvent | PointerEvent) => void;
  sortBy: readonly SortItem[];
  someSelected: boolean;
  allSelected: boolean;
  getSortIcon: (
    column: InternalDataTableHeader,
  ) => string | (string | [string, number])[] | (new () => unknown) | FunctionalComponent;
};

export type TableSlots<T extends Record<string, unknown>> = {
  actions?: (props: object) => void;
  'mobile-header-append'?: (props: object) => void;
} & { headers?: (props: HeadersSlotProps) => void } & {
  [K in `header.${string}`]: (props: HeaderSlotProps) => void;
} & {
  [K in `item.${string}`]: (
    props: ScopedSlotBase<T> & {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any;
    },
  ) => void;
} & {
  'header.data-table-select'?: (props: { allSelected: boolean; someSelected: boolean }) => void;
} & {
  'item.data-table-select'?: (
    props: ScopedSlotBase<T> & { toggleSelect: (internalItem: T, status: boolean) => void },
  ) => void;
};
